import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Logo } from '@/components/ui/Logo';
import { EmailIcon } from '@/assets/icons/EmailIcon';
import { LockIcon } from '@/assets/icons/LockIcon';
import { useLogin } from '@/hooks/useLogin';
import { isSafeReturnTo } from '@/lib/utils/return-to.utils';
import { setPendingLogin } from '@/lib/auth/pre-auth';
import { completeLoginSession } from '@/lib/auth/complete-login-session';
import { usePurchaseIntent } from '@/hooks/usePurchaseIntent';
import { withPurchaseIntent } from '@/lib/utils/purchase-intent.utils';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { JOURNEY_ROUTE } from '@/lib/constants/issue-report';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const returnToParam = searchParams.get('returnTo');
  const returnTo = isSafeReturnTo(returnToParam) ? returnToParam : undefined;
  const { mutate, isPending, isError } = useLogin();
  // A visitor who came from the Issue Report CTA keeps the intent through sign-up (FR-206).
  const intent = usePurchaseIntent(JOURNEY_ROUTE.LOGIN);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  /**
   * Normally step 1 of 2 (FR-04): no session is issued here, and the
   * challenge is handed to step 2 through the in-memory holder, NOT through
   * router state - React Router serialises router state into `history.state`,
   * which the browser restores on reload, and a half-authenticated credential
   * must not survive a reload (`docs/specs/features/identity-access/02-design.md` §11 decisions 2-3, corrected).
   *
   * While MYN-77 is temporarily disabled (client request, 2026-09-03), the
   * backend returns the full session directly instead of a challenge -
   * `outcome.requires2fa` is what tells the two branches apart, and the
   * disabled branch completes the login itself rather than navigating to a
   * step 2 that has nothing to do.
   */
  const onSubmit = (values: LoginFormValues) =>
    mutate(values, {
      onSuccess: (outcome) => {
        if (!outcome.requires2fa) {
          completeLoginSession(outcome, { queryClient, navigate, returnTo });
          return;
        }
        setPendingLogin({ challenge: outcome, email: values.email, returnTo });
        // No authenticator yet: set one up before any code can be asked for (MYN-241).
        navigate(outcome.setupRequired ? ROUTES.AUTH.TWO_FACTOR_SETUP : ROUTES.AUTH.TWO_FACTOR);
      },
    });

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] bg-gradient-to-b from-surface-from to-surface-to px-4 py-20">
      {intent && <IssueReportJourneySteps stage="account" route={JOURNEY_ROUTE.LOGIN} className="mb-10" />}
      {/* Logo */}
      <div className="mb-8">
        <div role="img" aria-label={UI_STRINGS.BRAND_NAME}>
          <Logo variant="color" height={48} aria-hidden />
        </div>
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl border border-input-border shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)] p-10 w-full max-w-[480px]">
        {/* Heading */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-navy">{UI_STRINGS.AUTH.LOGIN_HEADING}</h1>
          <p className="text-sm text-subtext mt-2">{UI_STRINGS.AUTH.LOGIN_SUBTITLE}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
          {/* Server-level error — generic, no credential hint */}
          {isError && (
            <p role="alert" className="text-sm text-danger text-center">
              {UI_STRINGS.AUTH.LOGIN_ERROR}
            </p>
          )}

          <div className="flex flex-col gap-4">
            <Input
              {...register('email')}
              label="Email address"
              type="email"
              autoComplete="email"
              placeholder="Email address"
              icon={<EmailIcon />}
              error={errors.email?.message}
            />

            <Input
              {...register('password')}
              label="Password"
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              icon={<LockIcon />}
              error={errors.password?.message}
            />

            <Button
              type="submit"
              variant="navy"
              disabled={isPending}
              className="w-full py-3.5 text-base drop-shadow-sm"
            >
              {isPending ? UI_STRINGS.AUTH.LOGIN_SUBMITTING : UI_STRINGS.AUTH.LOGIN_BUTTON}
            </Button>
          </div>

          {/* Footer links */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate">
              {UI_STRINGS.AUTH.NO_ACCOUNT}{' '}
              <Link
                to={withPurchaseIntent(ROUTES.AUTH.REGISTER, intent)}
                className="text-green font-semibold hover:text-dkgreen transition-colors"
              >
                {UI_STRINGS.AUTH.SIGN_UP}
              </Link>
            </p>
            <Link
              to={ROUTES.AUTH.FORGOT_PASSWORD}
              className="text-sm font-medium text-navy hover:text-teal transition-colors"
            >
              {UI_STRINGS.AUTH.FORGOT_PASSWORD}
            </Link>
          </div>
        </form>
      </div>

      {/* Trust badge */}
      <div className="flex items-center gap-1.5 mt-8">
        <LockIcon size={12} className="text-slate" />
        <p className="text-xs text-slate">{UI_STRINGS.AUTH.SECURE_BADGE}</p>
      </div>
    </div>
  );
}
